// src/context/brandVoice/draftModel.js — what to send, what changed, what conflicts. PURE.
//
// The draft is `{ fields: {path: value}, fieldsEn: {path: value}, voice: null|{label, axes} }`;
// the ORIGINALS are the read's own `field.value` / `field.value_en`, so nothing here stores a
// second copy of the server's state — which is why a draft survives a re-read and is then
// compared against the FRESH stored value. ⛔ Decision 1: dirty means DIFFERENT, never "touched".
import { sameValue, sameVoice } from './fieldValue.js';
import { toWire } from './wireShape.js';

const has = (o, k) => Object.prototype.hasOwnProperty.call(o || {}, k);

/** Paths whose draft differs from the stored value (IT, or EN for bucket D). */
export function dirtyPaths(fields, draft) {
  const out = new Set();
  (fields || []).forEach((f) => {
    if (has(draft.fields, f.field_path) && !sameValue(f, draft.fields[f.field_path], f.value)) out.add(f.field_path);
    if (f.policy === 'bucket_d' && has(draft.fieldsEn, f.field_path)
        && !sameValue(f, draft.fieldsEn[f.field_path], f.value_en)) out.add(f.field_path);
  });
  return out;
}

export function voiceDirty(original, draftVoice) {
  return !!draftVoice && !sameVoice(original, draftVoice);
}

const RECORDS = { widget: 'record_list' };

/**
 * ⛔⛔ v188: the `verified_claims` categories whose draft DIFFERS from the stored one — and ONLY
 * those. Every category a PUT sends is frozen (stamped `human`), re-translated (credits) and
 * overwrites its English category; the ones it omits are carried over untouched (backend
 * `_merge_claims`; contract: "the categories a request omits keep their stored records").
 * An emptied category is sent as `[]` (a clear). ⛔ A category ABSENT from the draft is never a
 * clear — it is simply not sent.
 */
export function changedClaims(field, draftValue) {
  const wire = toWire(field, draftValue && typeof draftValue === 'object' && !Array.isArray(draftValue) ? draftValue : {});
  const stored = field?.value && typeof field.value === 'object' && !Array.isArray(field.value) ? field.value : {};
  return Object.fromEntries(Object.entries(wire)
    .filter(([category, rows]) => !sameValue(RECORDS, rows, stored[category])));
}

/**
 * The PUT body parts for the dirty paths ONLY. A bucket-D field whose Italian changed but
 * English did not sends `fields` only (and vice versa) — ⛔ never an unchanged half.
 */
export function buildDocumentPayload(fields, draft) {
  const byPath = new Map((fields || []).map((f) => [f.field_path, f]));
  const out = { fields: {}, fieldsEn: {} };
  dirtyPaths(fields, draft).forEach((path) => {
    const f = byPath.get(path);
    // ⭐ `toWire`: the draft may hold the EDITOR's shape (Principi rows); the PUT takes the stored one.
    if (has(draft.fields, path) && !sameValue(f, draft.fields[path], f.value)) {
      if (f.widget === 'claims') {
        // ⛔⛔ v188: only the CHANGED categories — never the whole object (see changedClaims).
        const changed = changedClaims(f, draft.fields[path]);
        // ⚠️ No changed category → NO path (never `{}`): the field can still count as dirty
        // (tab dot, save-bar count) while the payload carries nothing for it.
        if (Object.keys(changed).length) out.fields[path] = changed;
      } else {
        out.fields[path] = toWire(f, draft.fields[path]);
      }
    }
    if (f.policy === 'bucket_d' && has(draft.fieldsEn, path) && !sameValue(f, draft.fieldsEn[path], f.value_en)) {
      out.fieldsEn[path] = toWire(f, draft.fieldsEn[path]);
    }
  });
  return out;
}

/** After a reload: dirty paths whose STORED value changed under the draft (decision 6). */
export function conflictsAfterReload(prevFields, nextFields, draft) {
  const prev = new Map((prevFields || []).map((f) => [f.field_path, f]));
  const out = new Set();
  dirtyPaths(nextFields, draft).forEach((path) => {
    const before = prev.get(path);
    const after = (nextFields || []).find((f) => f.field_path === path);
    if (before && after && (!sameValue(after, before.value, after.value)
        || (after.policy === 'bucket_d' && !sameValue(after, before.value_en, after.value_en)))) out.add(path);
  });
  return out;
}

export function tabHasChanges(tabKey, fields, dirty) {
  return (fields || []).some((f) => f.tab === tabKey && dirty.has(f.field_path));
}
